import React, { useState, useContext, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";
import {
  Button,
  Flex,
  Text,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Divider,
  Switch,
  useDisclosure,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { MdLogout, MdLock } from "react-icons/md";
import { IoMdMoon, IoMdSunny, IoIosArrowDown } from "react-icons/io";
import { ProfileIcon } from "components/Icons/Icons";
import { AuthContext } from "contexts/AuthContext";
import { ROUTES } from "constants/routes-path";

function ThemeControl(props) {
  const { ...rest } = props;
  const { t } = useTranslation();
  const { colorMode, setColorMode } = useColorMode();
  const [isLight, setIsLight] = useState(colorMode === "light");

  function toggleMode() {
    setIsLight(!isLight);
  }

  useEffect(() => {
    setTimeout(() => setColorMode(isLight ? "light" : "dark"), 100);
  }, [isLight]);

  return (
    <Flex px="2" pt="2" gap="2" direction="row" align="center" {...rest}>
      <Text>
        {t("admin.labels.theme")}
      </Text>

      <Flex gap="2" direction="row" align="center">
        <Icon as={IoMdMoon} />
        <Switch size="md" isChecked={isLight} onChange={toggleMode} />
        <Icon as={IoMdSunny} />
      </Flex>
    </Flex>
  );
}

export default function HeaderLinks(props) {
  const { variant, children, fixed, scrolled, secondary, ...rest } = props;
  const { t } = useTranslation();
  const { isOpen, onToggle } = useDisclosure();
  const { colorMode } = useColorMode();
  const { user, logout } = useContext(AuthContext);
  const dropIconRef = useRef(undefined);

  const menuBg = useColorModeValue("white", "navy.800");
  let navbarIcon = fixed && scrolled ? useColorModeValue("gray.700", "gray.200")
    : useColorModeValue("white", "gray.200");

  if (secondary) {
    navbarIcon = "white";
  }

  return (
    <Flex direction="row" align="center" {...rest}>
      <Flex gap="16px" direction="row" align="center">
        {user ? (<>
          <Popover placement="bottom" isOpen={isOpen} onClose={onToggle}>
            <PopoverTrigger>
              <Button
                px="0"
                fontWeight="bold"
                variant="no-effects"
                color={navbarIcon}
                textShadow="1px 1px 4px rgba(0,0,0,0.6)"
                leftIcon={<ProfileIcon color={navbarIcon} w="22px" h="22px" style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.7))" }} />}
                rightIcon={<Icon innerref={dropIconRef} color={navbarIcon} transform={isOpen ? "rotate(180deg)" : undefined} transition="0.2s linear" as={IoIosArrowDown} style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.7))" }}/>}
                onClick={onToggle}
              >
                {user.pessoa.nome}
              </Button>
            </PopoverTrigger>

            <PopoverContent w="100%" bg={menuBg}>
              <PopoverBody>
                <Flex gap="3" direction="column" align="center">
                  <ThemeControl />

                  <NavLink to={ROUTES.passwordChangePath}>
                    <Button
                      w="100%"
                      fontSize="md"
                      fontWeight="normal"
                      borderRadius="8px"
                      variant={colorMode}
                    >
                      <Flex w="100%" direction="row" align="center" justify="space-between">
                        <Flex />
                        <Text>{t("admin.labels.change-password")}</Text>
                        <Icon as={MdLock} ml="8px" />
                      </Flex>
                    </Button>
                  </NavLink>
                  <Divider />
                  <Button
                    w="100%"
                    fontSize="md"
                    fontWeight="normal"
                    borderRadius="8px"
                    variant={colorMode}
                    onClick={logout}
                  >
                    <Flex w="100%" direction="row" align="center" justify="space-between">
                      <Flex />
                      <Text>{t("admin.actions.logout")}</Text>
                      <Icon as={MdLogout} />
                    </Flex>
                  </Button>
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </>) : (
          <NavLink to={ROUTES.login}>
            <Button
              px="0px"
              variant="no-effects"
              color={navbarIcon}
              leftIcon={<ProfileIcon color={navbarIcon} w="22px" h="22px" />}
            >
              <Text>
                {t("admin.actions.login")}
              </Text>
            </Button>
          </NavLink>
        )}
      </Flex>
    </Flex>
  );
}

HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
};
